import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianFeesHook, useGuardianPayFeeHook } from '../../hooks/useGuardianFeesHook';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GuardianFee } from '../../../../../apis/guardian/sub-apis/useGuardianFeesService';
import { CheckCircle, Pending, Warning, Payment, Search, Clear, Receipt, ExpandMore, ExpandLess } from '@mui/icons-material';
import { TStatus } from '../../../../../apis/finance/finance.interfaces';
import { localStorageGet } from '../../../../../utils/localStorage';
import { ILoggedInUser } from '../../../../../apis/auth/auth.interfaces';

const GuardianFeesView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId, children } = useGuardianState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [academicYearFilter, setAcademicYearFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedFee, setSelectedFee] = useState<GuardianFee | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('online');
  const [transactionId, setTransactionId] = useState<string>('');
  const [transactionDateTime, setTransactionDateTime] = useState<Dayjs | null>(dayjs());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Get current user info
  const currentUser = useMemo<ILoggedInUser | null>(() => {
    const userFromStorage = localStorageGet('loggedInUser', undefined);
    return userFromStorage && typeof userFromStorage === 'object' ? (userFromStorage as ILoggedInUser) : null;
  }, []);

  // Get selected child name
  const selectedChildName = useMemo(() => {
    if (!selectedChildId) return '';
    const child = children.find((c) => c.id === selectedChildId);
    return child?.name || '';
  }, [selectedChildId, children]);

  // When search query exists, fetch all data for client-side filtering
  // Otherwise, use server-side pagination
  const shouldUseClientSideSearch = !!searchQuery;

  const { data, isLoading, error } = useGuardianFeesHook(
    selectedChildId || '',
    shouldUseClientSideSearch
      ? {
        // Fetch all data when searching client-side - use large limit to get all records
        page: 1,
        limit: 10000,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(academicYearFilter !== 'all' && { academicYear: academicYearFilter }),
      }
      : {
        // Use server-side pagination when no search query
        page,
        limit: pageSize,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(academicYearFilter !== 'all' && { academicYear: academicYearFilter }),
      }
  );

  const payFeeMutation = useGuardianPayFeeHook();

  const fees = useMemo(() => data?.data || [], [data?.data]);
  const serverTotalCount = data?.count ?? 0;

  const filteredFees = useMemo(() => {
    if (!searchQuery) return fees;
    const query = searchQuery.toLowerCase();
    return fees.filter(
      (fee) =>
        fee.feeType.toLowerCase().includes(query) ||
        fee.transactionId?.toLowerCase().includes(query) ||
        fee.id.toLowerCase().includes(query)
    );
  }, [fees, searchQuery]);

  // Use server total count when no search query, otherwise use filtered count
  const totalCount = useMemo(() => {
    if (shouldUseClientSideSearch) {
      return filteredFees.length;
    }
    return serverTotalCount;
  }, [shouldUseClientSideSearch, filteredFees.length, serverTotalCount]);

  const summary = useMemo(() => {
    const totalDue = filteredFees.reduce((sum, fee) => sum + fee.amount, 0);
    const paid = filteredFees.filter((f) => f.status === 'paid').reduce((sum, fee) => sum + fee.amount, 0);
    const pending = filteredFees.filter((f) => f.status === 'pending').reduce((sum, fee) => sum + fee.amount, 0);
    const overdue = filteredFees.filter((f) => f.status === 'overdue').reduce((sum, fee) => sum + fee.amount, 0);
    return { totalDue, paid, pending, overdue };
  }, [filteredFees]);

  const academicYears = useMemo(() => {
    const years = Array.from(new Set(fees.map((f) => new Date(f.dueDate).getFullYear().toString())));
    return years.sort().reverse();
  }, [fees]);

  const toggleRowExpansion = useCallback((feeId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(feeId)) {
        newSet.delete(feeId);
      } else {
        newSet.add(feeId);
      }
      return newSet;
    });
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'pending':
        return <Pending sx={{ color: 'warning.main' }} />;
      case 'overdue':
        return <Warning sx={{ color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDownloadReceipt = useCallback((fee: GuardianFee) => {
    if (!fee.paymentDate || !fee.transactionId) {
      alert(translateText('guardian.fees.receipt.notAvailable'));
      return;
    }
    // TODO: Implement actual receipt generation
    const receiptContent = `
      RECEIPT
      ===================
      Fee Type: ${fee.feeType}
      Amount: ₹${fee.amount.toLocaleString('en-IN')}
      Transaction ID: ${fee.transactionId}
      Payment Date: ${new Date(fee.paymentDate).toLocaleDateString()}
      Status: ${fee.status}
      ===================
    `;
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${fee.id}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [translateText]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'expand',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) return null; // Don't show expand icon for child rows
        const fee = row as GuardianFee & { hasComponents?: boolean };
        const isExpanded = expandedRows.has(fee.id);
        if (!fee.hasComponents) return null;
        return (
          <Box display="flex" alignItems="center" height="100%">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpansion(fee.id);
              }}
              data-testid={`expand-fee-${fee.id}`}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'feeType',
      headerName: translateText('guardian.fees.columns.feeType'),
      width: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          return (
            <Box display="flex" alignItems="center" height="100%" sx={{ pl: 4 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {row.componentName}
              </Typography>
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'amount',
      headerName: translateText('guardian.fees.columns.amount'),
      width: 150,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">₹{row.componentAmount?.toLocaleString('en-IN') || 0}</Typography>
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">₹{params.value.toLocaleString('en-IN')}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: translateText('guardian.fees.columns.dueDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          const dueDate = row.componentDueDate;
          if (!dueDate) return <Box display="flex" alignItems="center" height="100%">-</Box>;
          const date = new Date(dueDate);
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">
                {date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
          );
        }
        const date = new Date(params.value);
        const today = new Date();
        const isOverdue = date < today && params.row.status !== 'paid';
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              variant="body2"
              color={isOverdue ? 'error' : 'text.primary'}
              fontWeight={isOverdue ? 600 : 400}
            >
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: translateText('guardian.fees.columns.status'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          const status = row.componentStatus as TStatus;
          const getComponentStatusColor = (status: TStatus): 'success' | 'warning' | 'error' | 'default' => {
            switch (status) {
              case TStatus.PAID:
                return 'success';
              case TStatus.PARTIAL:
                return 'warning';
              case TStatus.UNPAID:
                return 'error';
              default:
                return 'default';
            }
          };
          const getComponentStatusLabel = (status: TStatus): string => {
            switch (status) {
              case TStatus.PAID:
                return translateText('guardian.fees.status.paid');
              case TStatus.PARTIAL:
                return translateText('guardian.fees.status.partial') || 'Partial';
              case TStatus.UNPAID:
                return translateText('guardian.fees.status.unpaid') || 'Unpaid';
              default:
                return status;
            }
          };
          return (
            <Box display="flex" alignItems="center" gap={1} height="100%">
              <Chip
                label={getComponentStatusLabel(status)}
                size="small"
                color={getComponentStatusColor(status)}
                variant="outlined"
              />
            </Box>
          );
        }
        const status = params.value as string;
        return (
          <Box display="flex" alignItems="center" gap={1} height="100%">
            {getStatusIcon(status)}
            <Chip
              label={translateText(`guardian.fees.status.${status}`)}
              size="small"
              color={getStatusColor(status)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'paymentDate',
      headerName: translateText('guardian.fees.columns.paymentDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {translateText('guardian.fees.components.paidAmount') || 'Paid'}: ₹{row.componentPaidAmount?.toLocaleString('en-IN') || 0}
              </Typography>
            </Box>
          );
        }
        const paymentDate = params.row.paymentDate;
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">
              {paymentDate
                ? new Date(paymentDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
                : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'transactionId',
      headerName: translateText('guardian.fees.columns.transactionId'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {translateText('guardian.fees.components.dueAmount') || 'Due'}: ₹{row.componentDueAmount?.toLocaleString('en-IN') || 0}
              </Typography>
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">{params.value || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: translateText('common.actions'),
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        if (row.isChild) {
          return null; // No actions for child rows
        }
        const fee = params.row as GuardianFee;
        return (
          <Box display="flex" alignItems="center" gap={1} height="100%">
            {fee.status === 'paid' ? (
              <Tooltip title={translateText('guardian.fees.receipt.downloadReceipt')}>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Receipt />}
                  onClick={() => {
                    handleDownloadReceipt(fee)
                  }}
                  data-testid={`pay-fee-btn-${fee.id}`}
                >
                  {translateText('guardian.fees.receipt.download')}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={translateText('guardian.fees.payment.paymentTooltip')}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Payment />}
                  onClick={() => {
                    setSelectedFee(fee);
                    setTransactionDateTime(dayjs());
                    setPaymentDialogOpen(true);
                  }}
                  data-testid={`pay-fee-btn-${fee.id}`}
                >
                  {translateText('guardian.fees.pay')}
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [translateText, expandedRows, toggleRowExpansion, handleDownloadReceipt]);

  // Apply client-side pagination when searching, otherwise use server-side paginated data
  const paginatedFees = useMemo(() => {
    if (shouldUseClientSideSearch) {
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      return filteredFees.slice(start, end);
    }
    return filteredFees;
  }, [shouldUseClientSideSearch, filteredFees, page, pageSize]);

  const rows = useMemo(() => {
    const expandedRowsList: any[] = [];

    paginatedFees.forEach((fee) => {
      // Add parent row
      expandedRowsList.push({
        ...fee, // Include full fee data
        hasComponents: fee.feeComponents && fee.feeComponents.length > 0,
        isParent: true,
      });

      // Add child rows if expanded and has components
      if (expandedRows.has(fee.id) && fee.feeComponents && fee.feeComponents.length > 0) {
        fee.feeComponents.forEach((component) => {
          expandedRowsList.push({
            id: `${fee.id}-component-${component.id}`,
            parentId: fee.id,
            isChild: true,
            componentName: component.componentName,
            componentAmount: component.amount,
            componentPaidAmount: component.paidAmount,
            componentDueAmount: component.amount - component.paidAmount,
            componentDueDate: component.dueDate,
            componentStatus: component.status,
            description: component.description,
          });
        });
      }
    });

    return expandedRowsList;
  }, [paginatedFees, expandedRows]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (searchQuery) {
      setPage(1);
    }
  }, [searchQuery]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handlePayment = () => {
    if (!selectedFee || !selectedChildId || !transactionDateTime) return;

    payFeeMutation.mutate(
      {
        studentId: selectedChildId,
        feeId: selectedFee.id,
        paymentData: {
          amount: selectedFee.amount,
          paymentMethod,
          transactionDateTime: transactionDateTime.toISOString(),
          ...(transactionId && { transactionId }),
        },
      },
      {
        onSuccess: () => {
          setPaymentDialogOpen(false);
          setSelectedFee(null);
          setTransactionId('');
          setTransactionDateTime(dayjs());
        },
      }
    );
  };


  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-fees-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-fees-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-fees-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-fees-container">
      <Grid item xs={12}>
        <Typography variant="h5">
          {translateText('guardian.fees.title')}
        </Typography>
        {selectedChildId && (
          <Typography variant="body2" color="text.secondary">
            {translateText('guardian.fees.selectedChild')}: {selectedChildId}
          </Typography>
        )}
      </Grid>

      {/* Summary Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.fees.summary.totalDue')}
            </Typography>
            <Typography variant="h5" color="primary.main">
              ₹{summary.totalDue.toLocaleString('en-IN')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.fees.summary.paid')}
            </Typography>
            <Typography variant="h5" color="success.main">
              ₹{summary.paid.toLocaleString('en-IN')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.fees.summary.pending')}
            </Typography>
            <Typography variant="h5" color="warning.main">
              ₹{summary.pending.toLocaleString('en-IN')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.fees.summary.overdue')}
            </Typography>
            <Typography variant="h5" color="error.main">
              ₹{summary.overdue.toLocaleString('en-IN')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Search and Filters with Grid */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={2}
              data-testid="guardian-fees-filters"
            >
              <TextField
                fullWidth
                placeholder={translateText('guardian.fees.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        data-testid="guardian-fees-search-clear"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
                data-testid="guardian-fees-search"
              />
              <TextField
                select
                label={translateText('guardian.fees.filters.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 150 }}
                size="small"
                data-testid="guardian-fees-status-filter"
              >
                <MenuItem value="all">{translateText('common.all')}</MenuItem>
                <MenuItem value="paid">{translateText('guardian.fees.status.paid')}</MenuItem>
                <MenuItem value="pending">{translateText('guardian.fees.status.pending')}</MenuItem>
                <MenuItem value="overdue">{translateText('guardian.fees.status.overdue')}</MenuItem>
              </TextField>
              {academicYears.length > 0 && (
                <TextField
                  select
                  label={translateText('guardian.fees.filters.academicYear')}
                  value={academicYearFilter}
                  onChange={(e) => setAcademicYearFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                  size="small"
                  data-testid="guardian-fees-academic-year-filter"
                >
                  <MenuItem value="all">{translateText('common.all')}</MenuItem>
                  {academicYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>

            {/* Fees Grid */}
            {filteredFees.length === 0 && !isLoading ? (
              <Alert severity="info">
                {searchQuery
                  ? translateText('guardian.fees.noSearchResults')
                  : translateText('guardian.fees.noFees')}
              </Alert>
            ) : (
              <PaginatedGrid
                rows={rows}
                columns={columns}
                loading={isLoading}
                dataTestId="guardian-fees-grid"
                serverSide={!shouldUseClientSideSearch}
                totalCount={totalCount}
                currentPage={page}
                currentPageSize={pageSize}
                onPageChange={handlePageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTableCell-root': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& tr[data-paginated-grid-row][data-is-child="true"]': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                  '& tr.child-row': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                }}
                getRowClassName={(params) => {
                  const row = params.row as any;
                  return row.isChild ? 'child-row' : '';
                }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedFee(null);
          setTransactionId('');
          setTransactionDateTime(dayjs());
        }}
        maxWidth="sm"
        fullWidth
        data-testid="guardian-fees-payment-dialog"
      >
        <DialogTitle>{translateText('guardian.fees.payment.title')}</DialogTitle>
        <DialogContent>
          {selectedFee && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  {/* Fee Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.fees.columns.feeType')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedFee.feeType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.fees.columns.amount')}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      ₹{selectedFee.amount.toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.fees.columns.dueDate')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedFee.dueDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Grid>
                  {selectedChildName && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {translateText('guardian.fees.payment.studentName')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedChildName}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Payment Details Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      {translateText('guardian.fees.payment.paymentDetails')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label={translateText('guardian.fees.payment.paymentMethod')}
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      size="small"
                      data-testid="guardian-fees-payment-method"
                    >
                      <MenuItem value="online">{translateText('guardian.fees.payment.methods.online')}</MenuItem>
                      <MenuItem value="cash">{translateText('guardian.fees.payment.methods.cash')}</MenuItem>
                      <MenuItem value="cheque">{translateText('guardian.fees.payment.methods.cheque')}</MenuItem>
                      <MenuItem value="bank_transfer">{translateText('guardian.fees.payment.methods.bankTransfer')}</MenuItem>
                    </TextField>
                  </Grid>
                  {paymentMethod !== 'cash' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={translateText('guardian.fees.payment.transactionId')}
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        size="small"
                        data-testid="guardian-fees-transaction-id"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Transaction Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      {translateText('guardian.fees.payment.transactionInfo')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box data-testid="guardian-fees-transaction-datetime">
                      <DateTimePicker
                        label={translateText('guardian.fees.payment.transactionDateTime')}
                        value={transactionDateTime}
                        onChange={(newValue) => setTransactionDateTime(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            required: true,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  {currentUser && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {translateText('guardian.fees.payment.paidBy')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {currentUser.firstName} {currentUser.lastName} {currentUser.username ? `(${currentUser.username})` : ''}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 4 }}>
          <Button
            size="small"
            onClick={() => {
              setPaymentDialogOpen(false);
              setSelectedFee(null);
              setTransactionId('');
              setTransactionDateTime(dayjs());
            }}
            data-testid="guardian-fees-payment-cancel"
          >
            {translateText('common.cancel')}
          </Button>
          <Button
            size="small"
            onClick={handlePayment}
            variant="contained"
            disabled={payFeeMutation.isPending || !transactionDateTime}
            data-testid="guardian-fees-payment-submit"
          >
            {payFeeMutation.isPending ? translateText('common.processing') : translateText('guardian.fees.payment.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default GuardianFeesView;
